import { useMemo } from 'react'
import { collection, query, where } from 'firebase/firestore'
import type { Query } from 'firebase/firestore'
import { db } from '../lib/firebase'
import { useFirestoreCollection } from './useFirestore'
import { useBlockedUsers } from '../contexts/BlockedUsersContext'
import type { EventTickets } from '../types'

export interface TicketMarks {
  /** Faro users with an accepted ticket in a ledger the viewer can read. */
  holderIds: ReadonlySet<string>
  /** Given a ticket they haven't confirmed yet (and holding none). */
  pendingIds: ReadonlySet<string>
}

/**
 * Who holds or has been given a ticket, per event, across every ledger the
 * viewer can read (their own + their crew's), limited to the viewer and
 * `scopeIds` (whose profiles the caller has) — the marks the people lists show.
 */
export function useTicketMarksByEvent(uid: string | null, scopeIds: string[]): ReadonlyMap<string, TicketMarks> {
  const mineRef = useMemo(
    () => (uid ? (query(collection(db, 'eventTickets'), where('userId', '==', uid)) as Query<EventTickets>) : null),
    [uid],
  )
  const crewRef = useMemo(
    () => (uid ? (query(collection(db, 'eventTickets'), where('viewerIds', 'array-contains', uid)) as Query<EventTickets>) : null),
    [uid],
  )
  const mine = useFirestoreCollection<EventTickets>(mineRef)
  const crew = useFirestoreCollection<EventTickets>(crewRef)
  const { isBlocked } = useBlockedUsers()

  return useMemo(() => {
    const scope = new Set(scopeIds)
    if (uid) scope.add(uid)
    const shown = (id: string) => scope.has(id) && !isBlocked(id)
    const byEvent = new Map<string, { holderIds: Set<string>; pendingIds: Set<string> }>()
    const ledgers = [...mine.data, ...crew.data.filter(l => l.userId !== uid && !isBlocked(l.userId))]
    for (const l of ledgers) {
      let marks = byEvent.get(l.eventId)
      if (!marks) byEvent.set(l.eventId, marks = { holderIds: new Set(), pendingIds: new Set() })
      for (const id of l.holderIds ?? []) if (shown(id)) marks.holderIds.add(id)
      for (const id of l.pendingIds ?? []) if (shown(id)) marks.pendingIds.add(id)
    }
    for (const [eventId, marks] of byEvent) {
      for (const id of marks.holderIds) marks.pendingIds.delete(id)
      if (marks.holderIds.size === 0 && marks.pendingIds.size === 0) byEvent.delete(eventId)
    }
    return byEvent
  }, [uid, scopeIds, mine.data, crew.data, isBlocked])
}
